import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { NavigationEnd, Router } from '@angular/router'
import { ApiService, LoginResponse } from './api.service'
import { Observable, tap } from 'rxjs'
import { filter } from 'rxjs/operators'

interface AuthUser {
  id: string
  name: string
  email: string
  role: 'customer' | 'seller' | 'admin' | 'super-admin' | string
}

type SessionScope = 'user' | 'admin'

const USER_PROFILE_KEY = 'user_profile'
const ADMIN_PROFILE_KEY = 'admin_profile'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  currentUser = signal<AuthUser | null>(null)

  isLoggedIn = computed(() => !!this.currentUser())
  isSuperAdmin = computed(() => this.currentUser()?.role === 'super-admin')
  isAdmin = computed(() => this.currentUser()?.role === 'admin' || this.currentUser()?.role === 'super-admin')
  isSeller = computed(() => this.currentUser()?.role === 'seller')

  constructor(
    private apiService: ApiService,
    private router: Router
  ) {
    this.syncCurrentUser()
    this.refreshSession()
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.syncCurrentUser())
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.apiService.userLogin(email, password).pipe(
      tap(response => {
        this.setSession(response, 'user')
      })
    )
  }

  staffLogin(identifier: string, password: string): Observable<LoginResponse> {
    return this.apiService.login(identifier, password).pipe(
      tap(response => {
        this.setSession(response, 'admin')
      })
    )
  }

  adminLogin(email: string, password: string): Observable<LoginResponse> {
    return this.apiService.adminLogin(email, password).pipe(
      tap(response => {
        this.setSession(response, 'admin')
      })
    )
  }

  logout(scope: 'auto' | SessionScope = 'auto') {
    const resolvedScope = scope === 'auto' ? this.getActiveScope() : scope
    const lastRole = this.getStoredUser(resolvedScope)?.role

    this.apiService.logout().subscribe({
      error: () => {
        // Session is already invalid or expired on server; local cleanup still applies.
      }
    })
    this.clearSession(resolvedScope)
    this.syncCurrentUser()

    if (lastRole === 'admin' || lastRole === 'super-admin' || lastRole === 'seller') {
      this.router.navigate(['/admin-login'])
    } else {
      this.router.navigate(['/'])
    }
  }

  getStoredUser(scope: SessionScope): AuthUser | null {
    const profileKey = scope === 'admin' ? ADMIN_PROFILE_KEY : USER_PROFILE_KEY
    const userStr = localStorage.getItem(profileKey)
    if (!userStr) return null
    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  }

  private setSession(response: LoginResponse, scope: SessionScope) {
    const profileKey = scope === 'admin' ? ADMIN_PROFILE_KEY : USER_PROFILE_KEY
    localStorage.setItem(profileKey, JSON.stringify(response.user))
    this.syncCurrentUser()
  }

  private clearSession(scope: SessionScope) {
    const profileKey = scope === 'admin' ? ADMIN_PROFILE_KEY : USER_PROFILE_KEY
    localStorage.removeItem(profileKey)
  }

  getActiveScope(): SessionScope {
    const path = (this.router.url || '').split('?')[0].split('#')[0]
    if (
      path.startsWith('/admin') ||
      path.startsWith('/super-admin') ||
      path.startsWith('/admin-login')
    ) {
      return 'admin'
    }
    return 'user'
  }

  private syncCurrentUser() {
    const scope = this.getActiveScope()
    this.currentUser.set(this.getStoredUser(scope))
  }

  private refreshSession() {
    const user = this.getStoredUser('user') || this.getStoredUser('admin')
    if (!user) return

    this.apiService.getMe().subscribe({
      next: ({ user: me }) => {
        const scope: SessionScope = ['admin', 'super-admin', 'seller'].includes(me.role) ? 'admin' : 'user'
        const profileKey = scope === 'admin' ? ADMIN_PROFILE_KEY : USER_PROFILE_KEY
        const otherProfileKey = scope === 'admin' ? USER_PROFILE_KEY : ADMIN_PROFILE_KEY
        localStorage.setItem(profileKey, JSON.stringify(me))
        localStorage.removeItem(otherProfileKey)
        this.syncCurrentUser()
      },
      error: (error: HttpErrorResponse) => {
        if (error?.status === 401) {
          localStorage.removeItem(USER_PROFILE_KEY)
          localStorage.removeItem(ADMIN_PROFILE_KEY)
          this.currentUser.set(null)
        } else {
          // Transient failures (offline/network/server) should not force logout.
          this.syncCurrentUser()
        }
      }
    })
  }
}
